<template>
    <div class="calificar-proveedor-container">
        <div class="unified-container">
            <!-- Header Section -->
            <div class="header-section">
                <div class="title-section">
                    <v-icon class="title-icon" color="white">mdi-account-check</v-icon>
                    <div class="title-text">
                        <h1 class="main-title">CALIFICAR PROVEEDORES</h1>
                        <p class="subtitle">Gestión de calificación de proveedores</p>
                    </div>
                </div>
            </div>

            <!-- Search and Filter Section -->
            <div class="search-section">
                <div class="search-filters">
                    <div class="search-card">
                        <v-text-field
                            v-model="search"
                            append-icon="mdi-magnify"
                            label="Buscar proveedor por NIT, nombre o contacto"
                            solo
                            flat
                            hide-details
                            class="search-field"
                        ></v-text-field>
                    </div>
                    <div class="filter-card">
                        <v-select
                            v-model="filtroEstado"
                            :items="opcionesFiltro"
                            item-text="text"
                            item-value="value"
                            label="Filtrar por estado"
                            solo
                            flat
                            hide-details
                            class="filter-field"
                        ></v-select>
                    </div>
                </div>
            </div>

            <!-- Table Section -->
            <div class="table-section">
                <div class="table-card">
                    <v-data-table
                        :headers="headers"
                        :items="proveedoresFiltrados"
                        :search="search"
                        class="modern-table"
                        :items-per-page="10"
                        :loading="loading"
                        loading-text="Cargando proveedores..."
                        :footer-props="{
                            'items-per-page-options': [10, 20, 50],
                            'items-per-page-text': 'Filas por página:',
                        }"
                    >
                        <template v-slot:no-data>
                            <v-alert
                                :value="true"
                                color="#F44336"
                                icon="mdi-alert"
                                outlined
                                type="warning"
                                prominent
                                border="left"
                            >
                                <strong>No</strong> se encontraron proveedores
                            </v-alert>
                        </template>

                        <template v-slot:[`item.estado`]="{ item }">
                            <v-chip
                                :color="item.estado === 'calificado' ? 'success' : 'warning'"
                                outlined
                                class="ma-2"
                            >
                                {{ item.estado === 'calificado' ? 'CALIFICADO' : 'PENDIENTE' }}
                            </v-chip>
                        </template>

                        <template v-slot:[`item.acciones`]="{ item }">
                            <div class="action-icons">
                                <v-btn
                                    class="evaluate-btn"
                                    small
                                    :color="item.estado === 'calificado' ? 'primary' : 'success'"
                                    @click="evaluarProveedor(item)"
                                >
                                    <v-icon left small>mdi-star-check</v-icon>
                                    {{ item.estado === 'calificado' ? 'Ver Calificación' : 'Calificar' }}
                                </v-btn>
                            </div>
                        </template>
                    </v-data-table>
                </div>
            </div>

            <!-- Promedio General debajo de la tabla -->
            <div class="promedio-general-section" v-if="promedioGeneral > 0">
                <div class="promedio-general-card">
                    <div class="promedio-general-label">
                        <v-icon color="#3b82f6">mdi-chart-line</v-icon>
                        <span>PROMEDIO GENERAL DE TODOS LOS PROVEEDORES</span>
                    </div>
                    <div class="promedio-general-valor">
                        <span class="promedio-numero">{{ promedioGeneral.toFixed(1) }}</span>
                        <span class="promedio-max">/ 5.0</span>
                    </div>
                </div>
            </div>

            <!-- Dialog para Calificar Proveedor -->
            <v-dialog v-model="dialogCalificar" max-width="950px" persistent overlay-color="black" overlay-opacity="0.8">
                <v-card>
                    <v-card-title class="nav">
                        <span class="headline" style="color: white">
                            <v-icon color="white">mdi-star-circle</v-icon>
                            Calificar Proveedor
                        </span>
                    </v-card-title>

                    <v-card-text class="pa-6">
                        <v-card class="mb-6" outlined>
                            <v-card-text>
                                <v-row dense>
                                    <v-col cols="12" md="4">
                                        <div class="info-label">NIT</div>
                                        <div class="info-value">{{ proveedorSeleccionado?.nit }}</div>
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <div class="info-label">Empresa</div>
                                        <div class="info-value">{{ proveedorSeleccionado?.nombre_empresa }}</div>
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <div class="info-label">Contacto</div>
                                        <div class="info-value">{{ proveedorSeleccionado?.contacto }}</div>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>

                        <div class="form-type-section mb-6">
                            <div class="form-type-title">
                                <v-icon color="#3b82f6">mdi-format-list-bulleted-type</v-icon>
                                <span>Seleccione el tipo de formulario de evaluación</span>
                            </div>
                            <v-select
                                v-model="tipoFormulario"
                                :items="tiposFormulario"
                                item-text="text"
                                item-value="value"
                                label="Tipo de Formulario"
                                outlined
                                dense
                                placeholder="Seleccione una opción"
                                @change="resetearPreguntas"
                            ></v-select>
                        </div>

                        <v-form ref="formEvaluacion" v-model="formValid" v-if="tipoFormulario">
                            <div class="font-weight-bold mb-4 text-center" style="font-size: 20px;">
                                <v-icon color="primary" class="mr-2">mdi-clipboard-list</v-icon>
                                {{ tituloFormulario }}
                                <span class="caption ml-2">({{ preguntasActuales.length }} preguntas)</span>
                            </div>

                            <v-card class="questions-card" outlined>
                                <v-card-text class="pa-0">
                                    <v-simple-table class="questions-table">
                                        <thead>
                                            <tr>
                                                <th width="60%">PREGUNTA</th>
                                                <th width="40%" class="text-center">CALIFICACIÓN</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="pregunta in preguntasActuales" :key="pregunta.id">
                                                <td class="pregunta-text">{{ pregunta.pregunta }}</td>
                                                <td class="text-center">
                                                    <div class="calificacion-botones">
                                                        <v-btn
                                                            v-for="n in 5"
                                                            :key="n"
                                                            :class="['calif-btn', { 'calif-btn-active': pregunta.valor === n }]"
                                                            small
                                                            fab
                                                            depressed
                                                            @click="pregunta.valor = n; calcularPromedio()"
                                                        >
                                                            {{ n }}
                                                        </v-btn>
                                                    </div>
                                                </td>
                                             </tr>
                                        </tbody>
                                    </v-simple-table>
                                </v-card-text>
                            </v-card>

                            <div class="promedio-formulario">
                                <span class="promedio-formulario-label">PROMEDIO DEL FORMULARIO:</span>
                                <span class="promedio-formulario-valor">{{ promedioTotal.toFixed(1) }}</span>
                                <span class="promedio-formulario-max">/ 5.0</span>
                                <span class="promedio-formulario-detalle ml-2">
                                    ({{ preguntasCalificadas }} de {{ preguntasActuales.length }} preguntas calificadas)
                                </span>
                            </div>

                            <v-card class="observations-card mt-6" outlined>
                                <v-card-title class="observations-title">
                                    <v-icon left color="#3b82f6">mdi-comment-text</v-icon>
                                    Observaciones
                                </v-card-title>
                                <v-card-text>
                                    <v-textarea
                                        v-model="observaciones"
                                        label="Observaciones adicionales"
                                        placeholder="Escriba aquí sus observaciones sobre el proveedor..."
                                        rows="3"
                                        counter="500"
                                        maxlength="500"
                                        outlined
                                        dense
                                    ></v-textarea>
                                </v-card-text>
                            </v-card>
                        </v-form>
                    </v-card-text>

                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text @click="cerrarCalificar">
                            Cancelar
                        </v-btn>
                            <v-btn
                                color="primary"
                                @click="guardarCalificacion"
                                :disabled="!formValid || !tipoFormulario || promedioTotal === 0 || calificando || !todasPreguntasCalificadas"
                                :loading="calificando"
                            >
                                <v-icon left>mdi-content-save</v-icon>
                                Guardar Calificación
                            </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- Dialog para ver evaluación existente -->
            <v-dialog v-model="dialogVerEvaluacion" max-width="900px" overlay-color="black" overlay-opacity="0.8">
                <v-card>
                    <v-card-title class="nav">
                        <span class="headline" style="color: white">
                            <v-icon color="white">mdi-star-box</v-icon>
                            Calificación del Proveedor
                        </span>
                    </v-card-title>

                    <v-card-text class="pa-6" v-if="evaluacionExistente">
                        <v-card class="mb-4" outlined>
                            <v-card-text>
                                <v-row dense>
                                    <v-col cols="12" md="4">
                                        <div class="info-label">NIT</div>
                                        <div class="info-value">{{ proveedorSeleccionado?.nit }}</div>
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <div class="info-label">Empresa</div>
                                        <div class="info-value">{{ proveedorSeleccionado?.nombre_empresa }}</div>
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <div class="info-label">Tipo</div>
                                        <div class="info-value">{{ getTipoFormularioTexto(evaluacionExistente.tipo) }}</div>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>

                        <v-card class="mb-4" outlined v-if="evaluacionExistente.nombreUsuario">
                            <v-card-title class="pa-3 pb-0">Calificado por</v-card-title>
                            <v-card-text>
                                <v-row dense>
                                    <v-col cols="12" md="4">
                                        <div class="info-label">Nombre</div>
                                        <div class="info-value">{{ evaluacionExistente.nombreUsuario || 'No disponible' }}</div>
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <div class="info-label">Área</div>
                                        <div class="info-value">{{ evaluacionExistente.nombreArea || 'No disponible' }}</div>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>

                        <div class="promedio-ver">
                            <span class="promedio-ver-label">PROMEDIO GENERAL:</span>
                            <span class="promedio-ver-valor">{{ evaluacionExistente.promedio.toFixed(1) }}</span>
                            <span class="promedio-ver-max">/ 5.0</span>
                        </div>

                        <v-simple-table dense>
                            <thead>
                                <tr>
                                    <th>Pregunta</th>
                                    <th class="text-center">Calificación</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(resp, idx) in evaluacionExistente.respuestas" :key="idx">
                                    <td class="pregunta-text-ver">{{ resp.pregunta }}</td>
                                    <td class="text-center">
                                        <div class="calificacion-botones-ver">
                                            <v-btn
                                                v-for="n in 5"
                                                :key="n"
                                                :class="['calif-btn-ver', { 'calif-btn-ver-active': resp.valor >= n }]"
                                                small
                                                fab
                                                depressed
                                                disabled
                                            >
                                                {{ n }}
                                            </v-btn>
                                        </div>
                                        <span class="ml-2 font-weight-bold">({{ resp.valor }}/5)</span>
                                    </td>
                                 </tr>
                            </tbody>
                        </v-simple-table>

                        <v-card v-if="evaluacionExistente.observaciones" class="mt-4" outlined>
                            <v-card-title class="pa-3 pb-0">Observaciones</v-card-title>
                            <v-card-text>{{ evaluacionExistente.observaciones }}</v-card-text>
                        </v-card>

                        <div class="text-caption text-center mt-4">
                            Fecha de calificación: {{ formatearFecha(evaluacionExistente.fecha_calificacion) }}
                        </div>
                    </v-card-text>

                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text @click="dialogVerEvaluacion = false">Cerrar</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <v-snackbar v-model="snackbar" :bottom="true" :timeout="timeout" :color="snackbarColor" class="modern-snackbar">
                {{ snackbarText }}
                <v-btn color="white" text @click="snackbar = false">Cerrar</v-btn>
            </v-snackbar>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import env from "../env";

export default {
    name: "CalificarProveedor",
    data() {
        return {
            search: "",
            filtroEstado: "todos",
            proveedores: [],
            loading: false,
            dialogCalificar: false,
            dialogVerEvaluacion: false,
            proveedorSeleccionado: null,
            evaluacionExistente: null,
            calificando: false,
            formValid: false,
            tipoFormulario: null,
            observaciones: "",
            snackbar: false,
            snackbarText: "",
            snackbarColor: "success",
            timeout: 6000,
            
            opcionesFiltro: [
                { text: "Todos", value: "todos" },
                { text: "Calificados", value: "calificado" },
                { text: "Pendientes", value: "pendiente" }
            ],
            
            tiposFormulario: [
                { text: "SEGURIDAD E INFRAESTRUCTURA", value: "seguridad" },
                { text: "SERVICIOS PROFESIONALES (MÉDICOS, FINANCIEROS, DESARROLLO DE SISTEMAS, SOPORTE, REDES (TRASMISIONES DE SORTEOS))", value: "servicios" },
                { text: "DOTACIÓN. PAPELERÍA (TÉRMICA, ROLLOS, RASPAS)", value: "dotacion" }
            ],
            
            preguntasSeguridad: [
                { id: 1, pregunta: "Cumplimiento de las condiciones contractuales.", valor: null },
                { id: 2, pregunta: "Respuesta oportuna frente a los requerimientos realizados por la empresa.", valor: null },
                { id: 3, pregunta: "Asesoría y acompañamiento.", valor: null },
                { id: 4, pregunta: "Calidad de la labor ejecutada.", valor: null },
                { id: 5, pregunta: "Disponibilidad y buen estado de las herramientas y de las maquinarias.", valor: null },
                { id: 6, pregunta: "Cumplimiento de los requisitos aplicables para la operación de sus procesos.", valor: null },
                { id: 7, pregunta: "Disponibilidad del servicio en la operación.", valor: null },
                { id: 8, pregunta: "Uso de los elementos de protección personal y dotación, por parte del personal ejecutor.", valor: null },
                { id: 9, pregunta: "Orden, aseo y limpieza del puesto de trabajo y señalización del espacio de trabajo si se requiere.", valor: null }
            ],

            preguntasServicios: [
                { id: 1, pregunta: "Cumplimiento de las condiciones contractuales.", valor: null },
                { id: 2, pregunta: "Respuesta oportuna frente a los requerimientos realizados por la empresa.", valor: null },
                { id: 3, pregunta: "Asesoría y acompañamiento.", valor: null },
                { id: 4, pregunta: "Calidad de la labor ejecutada.", valor: null },
                { id: 5, pregunta: "Oportunidad en los entregables.", valor: null },
                { id: 6, pregunta: "Cumplimiento de los requisitos aplicables para la operación de sus procesos.", valor: null },
                { id: 7, pregunta: "Disponibilidad del servicio en la operación.", valor: null }
            ],

            preguntasDotacion: [
                { id: 1, pregunta: "Cumplimiento de las condiciones contractuales.", valor: null },
                { id: 2, pregunta: "Respuesta oportuna frente a los requerimientos realizados por la empresa.", valor: null },
                { id: 3, pregunta: "Asesoría y acompañamiento. (Durante la prestación del servicio ofrece el soporte técnico necesario)", valor: null },
                { id: 4, pregunta: "Calidad del producto y/o servicio (contenido, medidas, acabados, funcionalidad, colores).", valor: null },
                { id: 5, pregunta: "Oportunidad en los entregables.", valor: null }
            ],
            
            headers: [
                { text: "#", value: "index", align: "center", width: "50px" },
                { text: "NIT", value: "nit", align: "left", width: "150px" },
                { text: "Nombre Empresa", value: "nombre_empresa", align: "left", width: "250px" },
                { text: "Contacto", value: "contacto", align: "left", width: "200px" },
                { text: "Teléfono", value: "telefono", align: "center", width: "120px" },
                { text: "Estado", value: "estado", align: "center", width: "130px" },
                { text: "Acciones", value: "acciones", align: "center", width: "150px", sortable: false }
            ]
        };
    },
    
    computed: {

        todasPreguntasCalificadas() {
            const preguntas = this.preguntasActuales;
            if (preguntas.length === 0) return false;
            
            // Verificar que todas las preguntas tengan un valor entre 1 y 5
            for (let i = 0; i < preguntas.length; i++) {
                if (preguntas[i].valor === null || preguntas[i].valor === 0) {
                    return false;
                }
            }
            return true;
        },
        tituloFormulario() {
            const titulos = {
                "seguridad": "SEGURIDAD E INFRAESTRUCTURA",
                "servicios": "SERVICIOS PROFESIONALES (MÉDICOS, FINANCIEROS, DESARROLLO DE SISTEMAS, SOPORTE, REDES (TRASMISIONES DE SORTEOS))",
                "dotacion": "DOTACIÓN. PAPELERÍA (TÉRMICA, ROLLOS, RASPAS)"
            };
            return titulos[this.tipoFormulario] || "";
        },
        
        preguntasActuales() {
            if (this.tipoFormulario === "seguridad") return this.preguntasSeguridad;
            if (this.tipoFormulario === "servicios") return this.preguntasServicios;
            if (this.tipoFormulario === "dotacion") return this.preguntasDotacion;
            return [];
        },
        
        proveedoresFiltrados() {
            if (this.filtroEstado === "todos") {
                return this.proveedores;
            }
            return this.proveedores.filter(p => p.estado === this.filtroEstado);
        },
        
        promedioTotal() {
            const preguntas = this.preguntasActuales;
            if (preguntas.length === 0) return 0;
            
            let suma = 0;
            for (let i = 0; i < preguntas.length; i++) {
                suma += preguntas[i].valor || 0;
            }
            
            return suma / preguntas.length;
        },
        
        preguntasCalificadas() {
            const preguntas = this.preguntasActuales;
            let calificadas = 0;
            for (let i = 0; i < preguntas.length; i++) {
                if (preguntas[i].valor !== null && preguntas[i].valor > 0) {
                    calificadas++;
                }
            }
            return calificadas;
        },
        
        promedioGeneral() {
            const calificados = this.proveedores.filter(p => p.promedio && p.promedio > 0);
            if (calificados.length === 0) return 0;
            const suma = calificados.reduce((a, b) => a + b.promedio, 0);
            return suma / calificados.length;
        }
    },
    
    mounted() {
        this.obtenerProveedores();
    },
    
    methods: {
        obtenerProveedores() {
            this.loading = true;
            axios.get(`${env.endpoint}user/obtenerProveedores`)
                .then(res => {
                    if (res.data.success) {
                        this.proveedores = res.data.proveedores.map((proveedor, index) => {
                            let calificacionData = null;
                            let calificado = false;
                            let promedio = null;
                            let respuestas = [];
                            let tipo = null;
                            let observaciones = "";
                            let fecha_calificacion = null;
                            let nombreUsuario = null;
                            let nombreArea = null;
                            
                            if (proveedor.calificacion) {
                                if (typeof proveedor.calificacion === 'object') {
                                    calificacionData = proveedor.calificacion;
                                } else if (typeof proveedor.calificacion === 'string') {
                                    try {
                                        calificacionData = JSON.parse(proveedor.calificacion);
                                    } catch(e) {
                                        calificacionData = null;
                                    }
                                }
                                
                                if (calificacionData) {
                                    calificado = true;
                                    promedio = calificacionData.promedio || null;
                                    respuestas = calificacionData.respuestas || [];
                                    tipo = calificacionData.tipo || null;
                                    observaciones = calificacionData.observaciones || "";
                                    fecha_calificacion = calificacionData.fecha_calificacion || null;
                                    nombreUsuario = calificacionData.nombreUsuario || null;
                                    nombreArea = calificacionData.nombreArea || null;
                                }
                            }
                            
                            return {
                                ...proveedor,
                                index: index + 1,
                                estado: calificado ? "calificado" : "pendiente",
                                promedio: promedio,
                                respuestas: respuestas,
                                tipo: tipo,
                                observaciones: observaciones,
                                fecha_calificacion: fecha_calificacion,
                                nombreUsuario: nombreUsuario,
                                nombreArea: nombreArea
                            };
                        });
                    } else {
                        this.mostrarSnackbar("Error al cargar proveedores", "error");
                    }
                })
                .catch(error => {
                    console.error("Error:", error);
                    this.mostrarSnackbar("Error de conexión al servidor", "error");
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        
        evaluarProveedor(proveedor) {
            this.proveedorSeleccionado = proveedor;
            
            if (proveedor.estado === "calificado") {
                this.evaluacionExistente = {
                    tipo: proveedor.tipo,
                    respuestas: proveedor.respuestas || [],
                    promedio: proveedor.promedio,
                    observaciones: proveedor.observaciones || "",
                    fecha_calificacion: proveedor.fecha_calificacion,
                    nombreUsuario: proveedor.nombreUsuario,
                    nombreArea: proveedor.nombreArea
                };
                this.dialogVerEvaluacion = true;
            } else {
                this.resetearCalificacion();
                this.dialogCalificar = true;
            }
        },
        
        resetearCalificacion() {
            this.tipoFormulario = null;
            this.observaciones = "";
            this.formValid = false;
            
            this.preguntasSeguridad.forEach(p => p.valor = null);
            this.preguntasServicios.forEach(p => p.valor = null);
            this.preguntasDotacion.forEach(p => p.valor = null);
            
            if (this.$refs.formEvaluacion) {
                this.$refs.formEvaluacion.resetValidation();
            }
        },
        
        resetearPreguntas() {
            const preguntas = this.preguntasActuales;
            preguntas.forEach(p => p.valor = null);
            this.formValid = false;
        },
        
        calcularPromedio() {
            this.formValid = this.promedioTotal > 0;
            this.$forceUpdate();
        },
        
        guardarCalificacion() {
            if (!this.tipoFormulario) {
                this.mostrarSnackbar("Por favor seleccione un tipo de formulario", "warning");
                return;
            }
            
            // Validar que TODAS las preguntas tengan calificación (1-5)
            const preguntasSinCalificar = this.preguntasActuales.filter(p => p.valor === null || p.valor === 0);
            if (preguntasSinCalificar.length > 0) {
                this.mostrarSnackbar(`Por favor califique todas las preguntas (faltan ${preguntasSinCalificar.length})`, "warning");
                return;
            }
            
            this.calificando = true;
            
            // Obtener información completa del usuario desde localStorage
            const usuario = {
                idUsuario: localStorage.getItem("id"),
                nombre: localStorage.getItem("nombre"),
                idRol: localStorage.getItem("idRol"),
                nombreRol: localStorage.getItem("nombreRol"),
                idArea: localStorage.getItem("idArea"),
                nombreArea: localStorage.getItem("area")
            };
            
            console.log("Información del usuario que califica:", usuario);
            
            const datos = {
                idProveedor: this.proveedorSeleccionado.idProveedor,
                tipo: this.tipoFormulario,
                respuestas: this.preguntasActuales.map(p => ({
                    pregunta: p.pregunta,
                    valor: p.valor || 0
                })),
                promedio: this.promedioTotal,
                observaciones: this.observaciones || "",
                fecha_calificacion: new Date().toISOString(),
                idUsuario: usuario.idUsuario,
                nombreUsuario: usuario.nombre,
                nombreArea: usuario.nombreArea
            };
            
            console.log("Datos a enviar al servidor:", datos);
            
            axios.post(`${env.endpoint}user/guardarCalificacionProveedor`, datos)
                .then(res => {
                    console.log("Respuesta del servidor:", res.data);
                    
                    if (res.data.success) {
                        this.mostrarSnackbar("Calificación guardada exitosamente", "success");
                        
                        // Actualizar el proveedor en la lista local inmediatamente
                        const index = this.proveedores.findIndex(p => p.idProveedor === this.proveedorSeleccionado.idProveedor);
                        if (index !== -1) {
                            this.proveedores[index].estado = "calificado";
                            this.proveedores[index].promedio = this.promedioTotal;
                            this.proveedores[index].tipo = this.tipoFormulario;
                            this.proveedores[index].respuestas = this.preguntasActuales.map(p => ({
                                pregunta: p.pregunta,
                                valor: p.valor || 0
                            }));
                            this.proveedores[index].observaciones = this.observaciones;
                            this.proveedores[index].fecha_calificacion = new Date().toISOString();
                            this.proveedores[index].nombreUsuario = usuario.nombre;
                            this.proveedores[index].nombreArea = usuario.nombreArea;
                        }
                        
                        this.cerrarCalificar();
                        this.obtenerProveedores();
                    } else {
                        this.mostrarSnackbar(res.data.message || "Error al guardar calificación", "error");
                    }
                })
                .catch(error => {
                    console.error("Error detallado:", error);
                    if (error.response) {
                        console.error("Respuesta del servidor:", error.response.data);
                        this.mostrarSnackbar(error.response.data.message || "Error de conexión al servidor", "error");
                    } else {
                        this.mostrarSnackbar("Error de conexión al servidor", "error");
                    }
                })
                .finally(() => {
                    this.calificando = false;
                });
        },
        
        cerrarCalificar() {
            this.dialogCalificar = false;
            this.proveedorSeleccionado = null;
            this.resetearCalificacion();
        },
        
        getTipoFormularioTexto(tipo) {
            const tipos = {
                "seguridad": "Seguridad e Infraestructura",
                "servicios": "Servicios Profesionales",
                "dotacion": "Dotación - Papelería"
            };
            return tipos[tipo] || tipo;
        },
        
        formatearFecha(fecha) {
            if (!fecha) return "No disponible";
            const date = new Date(fecha);
            return date.toLocaleDateString("es-ES", {
                year: "numeric",
                month: "long",
                day: "numeric"
            });
        },
        
        mostrarSnackbar(texto, tipo = "success") {
            this.snackbarText = texto;
            this.snackbarColor = tipo === "success" ? "#4caf50" : tipo === "error" ? "#f44336" : "#ff9800";
            this.snackbar = true;
        }
    }
};
</script>

<style scoped>
.calificar-proveedor-container {
    min-height: 100vh;
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
    padding: 1.5rem;
}

.unified-container {
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    background: white;
    border-radius: 0;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.header-section {
    background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
    padding: 2rem;
    color: white;
}

.title-section {
    display: flex;
    align-items: center;
    gap: 1.5rem;
}

.title-icon {
    width: 60px;
    height: 60px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.main-title {
    font-size: 1.8rem;
    font-weight: 700;
    margin: 0 0 0.5rem 0;
}

.subtitle {
    font-size: 1rem;
    margin: 0;
    opacity: 0.9;
}

.search-section {
    padding: 1.5rem 2rem;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
}

.search-filters {
    display: flex;
    gap: 1rem;
    align-items: center;
    flex-wrap: wrap;
}

.search-card {
    flex: 2;
    min-width: 250px;
}

.filter-card {
    flex: 1;
    min-width: 180px;
}

.search-field,
.filter-field {
    background: white !important;
    border-radius: 12px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
}

.table-section {
    padding: 0;
    background: white;
    width: 100%;
    overflow-x: auto;
}

.modern-table {
    background: white !important;
    min-width: 900px;
}

.modern-table >>> .v-data-table__wrapper {
    overflow-x: scroll !important;
}

.modern-table >>> .v-data-table-header th {
    color: #374151 !important;
    font-weight: 600 !important;
    font-size: 0.75rem !important;
    text-transform: uppercase !important;
    padding: 0.65rem 0.5rem !important;
    background: #f9fafb !important;
    border-bottom: 2px solid #e5e7eb !important;
}

.modern-table >>> .v-data-table__wrapper table tbody tr td {
    padding: 1rem 0.75rem !important;
    font-size: 0.875rem !important;
}

.action-icons {
    display: flex;
    justify-content: center;
}

.evaluate-btn {
    text-transform: none !important;
    font-weight: 500 !important;
    border-radius: 8px !important;
}

.promedio-general-section {
    padding: 1.5rem 2rem;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: flex-end;
}

.promedio-general-card {
    background: white;
    border-radius: 12px;
    padding: 1rem 2rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    border: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    gap: 2rem;
}

.promedio-general-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    color: #1e293b;
    font-size: 0.9rem;
}

.promedio-general-valor {
    display: flex;
    align-items: baseline;
    gap: 0.25rem;
}

.promedio-numero {
    font-size: 2rem;
    font-weight: 700;
    color: #3b82f6;
}

.promedio-max {
    font-size: 1rem;
    color: #64748b;
}

.nav {
    background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%) !important;
    color: white !important;
}

.info-label {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    color: #64748b;
}

.info-value {
    font-size: 1rem;
    font-weight: 500;
    color: #1e293b;
    margin-top: 0.25rem;
}

.form-type-section {
    padding: 1rem;
    background: #f8fafc;
    border-radius: 12px;
}

.form-type-title {
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.questions-card {
    margin-top: 1rem;
}

.questions-table {
    width: 100%;
}

.questions-table thead th {
    background: #f1f5f9;
    font-weight: 600;
    padding: 12px;
}

.questions-table tbody td {
    padding: 12px;
    border-bottom: 1px solid #e2e8f0;
}

.pregunta-text {
    font-size: 0.9rem;
    color: #334155;
}

.pregunta-text-ver {
    font-size: 0.85rem;
    color: #334155;
}

.calificacion-botones {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
    flex-wrap: wrap;
}

.calif-btn {
    width: 38px !important;
    height: 38px !important;
    min-width: 38px !important;
    background: white !important;
    color: #64748b !important;
    border: 2px solid #cbd5e1 !important;
    font-weight: 700 !important;
    font-size: 1rem !important;
    transition: all 0.2s ease !important;
}

.calif-btn-active {
    background: #3b82f6 !important;
    color: white !important;
    border-color: #3b82f6 !important;
    transform: scale(1.05);
}

.calif-btn:hover:not(.calif-btn-active) {
    background: #e2e8f0 !important;
    border-color: #94a3b8 !important;
}

.calificacion-botones-ver {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
    flex-wrap: wrap;
}

.calif-btn-ver {
    width: 36px !important;
    height: 36px !important;
    min-width: 36px !important;
    background: #f1f5f9 !important;
    color: #94a3b8 !important;
    border: 2px solid #e2e8f0 !important;
    font-weight: 700 !important;
    font-size: 0.9rem !important;
    cursor: default !important;
    opacity: 0.6;
}

.calif-btn-ver-active {
    background: #3b82f6 !important;
    color: white !important;
    border-color: #3b82f6 !important;
    opacity: 1;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.promedio-formulario {
    text-align: right;
    margin-top: 1rem;
    padding: 0.75rem 1rem;
    border-top: 1px solid #e2e8f0;
}

.promedio-formulario-label {
    font-weight: 600;
    color: #1e293b;
    margin-right: 0.75rem;
}

.promedio-formulario-valor {
    font-size: 1.5rem;
    font-weight: 700;
    color: #3b82f6;
}

.promedio-formulario-max {
    color: #64748b;
    margin-left: 0.25rem;
}

.promedio-formulario-detalle {
    font-size: 0.75rem;
    color: #64748b;
}

.promedio-ver {
    text-align: center;
    margin-bottom: 1.5rem;
    padding: 0.75rem;
    background: #f8fafc;
    border-radius: 8px;
}

.promedio-ver-label {
    font-weight: 600;
    color: #1e293b;
    margin-right: 0.75rem;
}

.promedio-ver-valor {
    font-size: 1.5rem;
    font-weight: 700;
    color: #3b82f6;
}

.promedio-ver-max {
    color: #64748b;
}

.observations-card {
    margin-top: 1.5rem;
}

.observations-title {
    background: #f8fafc;
}

.modern-snackbar {
    border-radius: 8px !important;
}

.v-dialog .v-card-actions .v-btn--text {
    border: 1px solid #cbd5e1 !important;
    background: transparent !important;
    color: #475569 !important;
}

.v-dialog .v-card-actions .v-btn--text:hover {
    background: #f8fafc !important;
    border-color: #94a3b8 !important;
}

@media (max-width: 768px) {
    .calificar-proveedor-container {
        padding: 0.75rem;
    }
    
    .header-section {
        padding: 1.25rem;
        text-align: center;
    }
    
    .title-section {
        flex-direction: column;
        text-align: center;
        gap: 0.75rem;
    }
    
    .main-title {
        font-size: 1.3rem;
    }
    
    .search-section {
        padding: 1rem;
    }
    
    .search-filters {
        flex-direction: column;
    }
    
    .search-card,
    .filter-card {
        width: 100%;
        min-width: auto;
    }
    
    .promedio-general-section {
        justify-content: center;
    }
    
    .promedio-general-card {
        flex-direction: column;
        gap: 0.5rem;
        text-align: center;
    }
    
    .calificacion-botones {
        gap: 0.25rem;
    }
    
    .calif-btn {
        width: 32px !important;
        height: 32px !important;
        min-width: 32px !important;
        font-size: 0.8rem !important;
    }
    
    .calif-btn-ver {
        width: 30px !important;
        height: 30px !important;
        min-width: 30px !important;
        font-size: 0.75rem !important;
    }
    
    .promedio-formulario {
        text-align: center;
    }
}
</style>